import React, { Component } from "react";
import { Modal, Col, Row } from "react-bootstrap";
import { graphql } from "react-apollo";
import gql from "graphql-tag";
import moment from "moment";
import * as Cookies from "es-cookie";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import { withApollo } from "react-apollo";
import jsPDF from 'jspdf';
import FeeReceiptModal from './FeeReceiptModal';
import { canEdit } from "../../../../utils/modulePermissions";

// /Fee-Structure/* → "Fee_Structure". Match on module_key only.
//
// studentPaymentsSection already gates showFeeModal, the only way this modal opens, so
// this is a backstop on the write itself. It uses the SILENT canEdit() rather than
// requireEdit() because this modal renders no Access Denied modal of its own — the denial
// is surfaced by the gated opener.
const FEE_STRUCTURE_MODULE = ["Fee_Structure"];

const currentDate = new Date().toJSON().slice(0, 10);
const fmtDate = (unix) => unix ? moment.unix(unix).format('DD/MM/YYYY') : '—';

const ADD_TERMS = gql`
  mutation($params: AddTerms) {
    addTerms(params: $params)
  }`;

const UPDATE_TERMS = gql`
  mutation($params: UpdateTerms) {
    updateTerms(params: $params)
  }`;

const GET_FEE_TYPES = gql`
    query($institute_id: Int) {
        getFeeTypes(institute_id: $institute_id) {
            id
            fee_type_name
            is_report
        }
    }
`;

const GET_ORGANISATION_PROFILE = gql`
    query($institution_id: Int!) {
        getOrganisationProfile(institution_id: $institution_id) {
            id
            organisation_name
            logo
            complete_address
        }
    }
`;

/*
  ── getStudentFeeByType(student_id, academic_year_id, fee_type_id) ────────────

  Scenario: 3 terms of ₹15000 each (total ₹50000 - ₹5000 discount = ₹45000)
    • Term 1 → Fully paid
    • Term 2 → Partially paid ₹10000, due entry of ₹5000 created
    • Term 3 → Pending

  BACKEND RULES:
  1. Regular terms  → is_due_entry: false, due_from_term_no: null
  2. Due entries    → is_due_entry: true,  due_from_term_no: <original term_no>
  3. Return terms array ORDERED by position (regular terms by term_no, due entry
     immediately after its parent term)
  4. paid_amount must be stored and returned for partial payment tracking

  {
    "data": {
      "getStudentFeeByType": {
        "total_fee": 50000,
        "discount": 5000,
        "no_of_terms": 3,
        "terms": [
          {
            "term_no": 1,
            "term_amount": 15000,
            "term_date": "1714521600",
            "is_paid": 1,
            "paid_amount": 15000,
            "paid_date": "1714521600",
            "payment_mode_id": 2,
            "payment_id": "RCP-001",
            "comments": "Online transfer",f
            "is_due_entry": false,
            "due_from_term_no": null
          },
          {
            "term_no": 2,
            "term_amount": 15000,
            "term_date": "1717113600",
            "is_paid": 0,
            "paid_amount": 10000,
            "paid_date": "1717200000",
            "payment_mode_id": 1,
            "payment_id": "RCP-002",
            "comments": "Partial cash payment",
            "is_due_entry": false,
            "due_from_term_no": null
          },
          {
            "term_no": 4,
            "term_amount": 5000,
            "term_date": null,
            "is_paid": 0,
            "paid_amount": 0,
            "paid_date": null,
            "payment_mode_id": null,
            "payment_id": null,
            "comments": null,
            "is_due_entry": true,
            "due_from_term_no": 2
          },
          {
            "term_no": 3,
            "term_amount": 15000,
            "term_date": "1719792000",
            "is_paid": 0,
            "paid_amount": 0,
            "paid_date": null,
            "payment_mode_id": null,
            "payment_id": null,
            "comments": null,
            "is_due_entry": false,
            "due_from_term_no": null
          }
        ]
      }
    }
  }
  ─────────────────────────────────────────────────────────────────────────────
*/
const GET_STUDENT_FEE_BY_TYPE = gql`
    query($student_id: Int, $academic_year_id: Int, $fee_type_id: Int) {
        getStudentFeeByType(student_id: $student_id, academic_year_id: $academic_year_id, fee_type_id: $fee_type_id) {
            total_fee
            discount
            no_of_terms
            terms {
                term_no
                student_payment_log_id
                term_amount
                term_discount
                internal_amount
                term_date
                is_paid
                paid_amount
                paid_date
                payment_mode_id
                payment_id
                comments
                is_due_entry
                due_from_term_no
            }
        }
    }
`;

const blankTerm = (termNo, amount = 0) => ({
    term_no: termNo,
    term_amount: amount,
    term_discount: 0,
    internal_amount: 0,
    term_date: "",
    is_paid: 0,
    paid_amount: 0,
    paid_date: "",
    payment_mode_id: "",
    comments: "",
    termAmountError: "",
    dateError: "",
});

const RESET_STATE = {
    feeType: 0,
    totalFee: "",
    feeAmount: "",
    discountFee: "",
    termsValue: "",
    feeTypeError: "",
    totalFeeError: "",
    discountFeeError: "",
    feeAmountError: "",
    termsValueError: "",
    paymentArray: [],
    paidTermsCount: 0,
    defaultArray: [],
    submitError: "",
    loadingSpinner: 0,
    currentStep: 1,
    isSubmitting: false,
    payInputAmounts: {},
    isExistingFee: false,
    termsConfigured: false,
    receiptModal: false,
    receiptItem: null,
    pendingChanges: false,
    showFeeTypeInPdf: {},
    manuallyEnabledTerms: {},
    termInputValues: {},
    fullPaymentMode: "",
};

class FeeStatusModal extends Component {
    constructor(props) {
        super(props);
        this.state = { dataClear: 0, orgProfile: {}, ...RESET_STATE };
    }

    componentDidMount() {
        this.loadOrgProfile();
    }

    loadOrgProfile = async () => {
        const cookieFallback = {
            organisation_name: Cookies.get("institutename") || "",
            logo: Cookies.get("institute_logo") || "",
            complete_address: Cookies.get("institute_address") || "",
        };
        const institutionId = parseInt(Cookies.get("institutionid"));
        if (!institutionId || isNaN(institutionId)) {
            this.setState({ orgProfile: cookieFallback });
            return;
        }
        try {
            const result = await this.props.client.query({
                query: GET_ORGANISATION_PROFILE,
                variables: { institution_id: institutionId },
                fetchPolicy: "network-only",
            });
            const data = result.data?.getOrganisationProfile;
            this.setState({
                orgProfile: data
                    ? {
                        organisation_name: data.organisation_name || "",
                        logo: data.logo || "",
                        complete_address: data.complete_address || "",
                    }
                    : cookieFallback,
            });
        } catch (err) {
            console.error("loadOrgProfile error:", err);
            this.setState({ orgProfile: cookieFallback });
        }
    };

    componentDidUpdate() {
        if (this.props.show === true && this.state.dataClear === 0) {
            this.setState({ dataClear: 1, ...RESET_STATE });
        }
        if (this.props.show === false && this.state.dataClear === 1) {
            this.setState({ dataClear: 0 });
        }
    }

    // Redistribute newFeeAmount across unpaid regular terms; leave paid, partial, and due entries untouched.
    adjustUnpaidTerms = (paymentArray, newFeeAmount) => {
        // A term is locked if it's paid, partially paid, OR has due entries (payments tracked via due-entry chain)
        const hasDueEntries = (t) => paymentArray.some(d => d.isDueEntry && d.dueFromTermNo == t.term_no);
        const isLocked = (t) => t.is_paid == 1 || (t.paid_amount || 0) > 0 || hasDueEntries(t);

        const lockedAmount = paymentArray
            .filter(t => !t.isDueEntry && isLocked(t))
            .reduce((s, t) => s + (parseInt(t.term_amount) || 0), 0);
        const unpaidCount = paymentArray.filter(t => !t.isDueEntry && !isLocked(t)).length;
        if (unpaidCount === 0) return paymentArray;
        const toDistribute = Math.max(0, newFeeAmount - lockedAmount);
        const perTerm = Math.floor(toDistribute / unpaidCount);
        const remainder = toDistribute - perTerm * unpaidCount;
        let idx = 0;
        return paymentArray.map(t => {
            if (t.isDueEntry || isLocked(t)) return t;
            idx++;
            return { ...t, term_amount: idx === unpaidCount ? perTerm + remainder : perTerm, termAmountError: "" };
        });
    };

    // Evenly split feeAmount across numTerms (1-based), preserving paid terms from existingTerms.
    buildPaymentArray(feeAmount, numTerms, existingTerms = []) {
        const fee = +feeAmount || 0;
        const n = parseInt(numTerms) || 0;
        if (n <= 0) return [];

        const paidTerms = existingTerms.filter(t => t.is_paid == 1 && !t.isDueEntry);
        const paidAmount = paidTerms.reduce((s, t) => s + (parseInt(t.term_amount) || 0), 0);
        const unpaidCount = n - paidTerms.length;

        if (unpaidCount <= 0) {
            return paidTerms.slice(0, n).map(t => ({ ...t, termAmountError: "", dateError: "" }));
        }

        const toSplit = fee - paidAmount;
        const perTerm = Math.floor(toSplit / unpaidCount);
        const remainder = toSplit - perTerm * unpaidCount;

        // For new fee entries (no existing terms), auto-assign due dates 3 months apart starting from today
        const isNewFee = existingTerms.length === 0;

        const result = [];
        let unpaidIdx = 0;

        for (let i = 1; i <= n; i++) {
            const ex = existingTerms.find(t => t.term_no === i && !t.isDueEntry);
            if (ex && ex.is_paid == 1) {
                result.push({ ...ex, termAmountError: "", dateError: "" });
            } else {
                unpaidIdx++;
                // Add remainder to the last unpaid term so totals always match
                const amount = unpaidIdx === unpaidCount ? perTerm + remainder : perTerm;
                if (ex) {
                    result.push({ ...ex, term_amount: amount, termAmountError: "", dateError: "" });
                } else {
                    let term_date = "";
                    if (isNewFee) {
                        term_date = moment().add((i - 1) * 3, 'months').startOf('day').unix().toString();
                    }
                    // Default "Others" to 3000 (capped below the term amount).
                    const internal_amount = Math.min(3000, Math.max(0, amount - 1));
                    result.push({ ...blankTerm(i, amount), term_date, internal_amount });
                }
            }
        }
        return result;
    }

    handleFeeFunction = async (e) => {
        const name = e?.target?.name;
        const value = e?.target?.value;

        if (name === "feeType") {
            const feeTypeId = parseInt(value);
            this.setState({
                feeType: value, feeTypeError: "", submitError: "",
                feeAmountError: "", termsValueError: "",
                totalFee: "", feeAmount: "", discountFee: "",
                termsValue: "", paymentArray: [], paidTermsCount: 0, defaultArray: [],
            });

            if (!feeTypeId) return;

            try {
                const { data } = await this.props.client.query({
                    query: GET_STUDENT_FEE_BY_TYPE,
                    variables: {
                        student_id: parseInt(this.props.stateData.student_id),
                        academic_year_id: parseInt(this.props.stateData.yearSearch),
                        fee_type_id: feeTypeId,
                    },
                    fetchPolicy: "network-only",
                });

                const res = data?.getStudentFeeByType;
                if (!res) return;

                const totalFee = res.total_fee ?? 0;
                const discountFee = res.discount ?? 0;
                const feeAmount = totalFee - discountFee;

                // Map API fields; infer isDueEntry from is_due_entry flag OR non-null due_from_term_no.
                // due_from_term_no is the most reliable indicator — it is set by the backend on every due
                // entry and is order-independent, so we don't misidentify the parent term even when the
                // backend returns a due entry before its parent in the array.
                // Fallback: occurrence counter for entries that lack both flags (legacy data).
                const _tc1 = {};
                const terms = (res.terms || []).map(t => {
                    const isExplicitDue = !!t.is_due_entry || t.due_from_term_no != null;
                    const tn = t.term_no;
                    let isDueEntry = isExplicitDue;
                    if (!isExplicitDue) {
                        const occ = _tc1[tn] || 0;
                        _tc1[tn] = occ + 1;
                        isDueEntry = occ > 0;
                    }
                    const dueFromTermNo = t.due_from_term_no != null ? t.due_from_term_no : isDueEntry ? tn : null;
                    // Default "Others" to 3000 for regular, unpaid terms that don't already have a value
                    // (capped below the pay amount). Due entries and already-paid terms keep their stored value.
                    const _netRemaining = ((parseInt(t.term_amount) || 0) - (parseInt(t.term_discount) || 0) - (parseInt(t.paid_amount) || 0));
                    const internalAmount = (!isDueEntry && t.is_paid != 1 && !(parseInt(t.internal_amount) > 0))
                        ? Math.min(3000, Math.max(0, _netRemaining - 1))
                        : (parseInt(t.internal_amount) || 0);
                    return { ...t, paid_amount: t.paid_amount || 0, term_discount: t.term_discount || 0, internal_amount: internalAmount, isDueEntry, dueFromTermNo, termAmountError: "", dateError: "" };
                });

                const paidTermsCount = terms.filter(t => t.is_paid && !t.isDueEntry).length;

                if (terms.length > 0) {
                    // Existing fee record — load with due entries preserved
                    const regularTermCount = terms.filter(t => !t.isDueEntry).length;
                    this.setState({
                        totalFee, discountFee, feeAmount,
                        termsValue: res.no_of_terms ?? "",
                        paymentArray: terms,
                        paidTermsCount,
                        defaultArray: terms,
                        isExistingFee: true,
                        termsConfigured: res.no_of_terms !== null,
                    });
                } else if (res.no_of_terms) {
                    // Fee structure exists (total_fee + no_of_terms set) but no terms saved yet —
                    // auto-build equal terms so the user can directly review and pay
                    const arr = this.buildPaymentArray(feeAmount, res.no_of_terms, []);
                    this.setState({
                        totalFee, discountFee, feeAmount,
                        termsValue: res.no_of_terms,
                        paymentArray: arr,
                        isExistingFee: false,
                        termsConfigured: true,
                    });
                } else {
                    // New fee entry — prefill totals, user enters terms count manually
                    this.setState({ totalFee, discountFee, feeAmount, isExistingFee: false });
                }
            } catch (err) {
                console.error("getStudentFeeByType error:", err);
            }

        } else if (name === "totalFee") {
            const newFeeAmount = Math.max(0, +value - +(this.state.discountFee || 0));
            this.setState({ totalFee: value, feeAmountError: "", submitError: "", feeAmount: newFeeAmount });
            if (value !== "" && this.state.termsValue) {
                const arr = this.state.paymentArray.length > 0
                    ? this.adjustUnpaidTerms(this.state.paymentArray, newFeeAmount)
                    : this.buildPaymentArray(newFeeAmount, this.state.termsValue, this.state.defaultArray);
                this.setState({ paymentArray: arr, pendingChanges: true });
            }

        } else if (name === "discountFee") {
            if (+value > +this.state.totalFee) {
                this.setState({ discountFeeError: "Discount cannot be greater than total fee", discountFee: +value });
                return;
            }
            const newFeeAmount = Math.max(0, +(this.state.totalFee || 0) - +value);
            this.setState({ discountFee: +value, discountFeeError: "", submitError: "", feeAmount: newFeeAmount });
            if (this.state.termsValue) {
                const arr = this.state.paymentArray.length > 0
                    ? this.adjustUnpaidTerms(this.state.paymentArray, newFeeAmount)
                    : this.buildPaymentArray(newFeeAmount, this.state.termsValue, this.state.defaultArray);
                this.setState({ paymentArray: arr, pendingChanges: true });
            }

        } else if (name === "terms") {
            const numTerms = parseInt(value) || 0;
            this.setState({ termsValue: value, termsValueError: "", submitError: "" });
            if (numTerms <= 0) return;

            if (numTerms <= this.state.paidTermsCount) {
                this.setState({ termsValueError: `Terms must be greater than ${this.state.paidTermsCount}` });
                return;
            }
            const arr = this.buildPaymentArray(this.state.feeAmount, numTerms, this.state.defaultArray);
            this.setState({ paymentArray: arr });

        }
    };

    onKeyPress = (event) => {
        const name = event?.target?.name;
        let maxLength = name === "totalFee" || name === "discountFee" ? 6 : name === "terms" ? 1 : 5;
        const keyCode = event.which || event.keyCode;
        const keyValue = String.fromCharCode(keyCode);
        if (!/^\d+$/.test(keyValue) || event.target.value.length >= maxLength) {
            event.preventDefault();
        }
    };

    handleDateChange = async (e, index, date) => {
        e.persist();
        const name = e?.target?.name;
        const arr = this.state.paymentArray.map((item, idx) => {
            if (idx !== index) return item;
            if (name === "term_date") {
                return { ...item, term_date: Math.floor(new Date(date).getTime() / 1000 - 19800).toString(), dateError: "" };
            }
            if (name === "paidDate") {
                return { ...item, paid_date: Math.floor(new Date(date).getTime() / 1000 - 19800).toString(), dateError: "" };
            }
            return item;
        });
        this.setState({ paymentArray: arr });
    };

    paymentCheckBox = (e, term, forDue = false) => {
        let array = [];
        let amountAlert = false;
        let dateAlert = false;
        let newDueEntry = null;
        const now = Math.floor(Date.now() / 1000).toString();

        // Use composite key so regular term and due entry with same term_no don't collide
        const payKey = forDue ? `${term}_due` : term;
        const enteredAmount = (this.state.payInputAmounts[payKey] !== undefined && this.state.payInputAmounts[payKey] > 0)
            ? parseInt(this.state.payInputAmounts[payKey]) || 0
            : null;

        // For due entries, target only the first unpaid one — already-paid dues must never be modified
        const targetItem = forDue
            ? this.state.paymentArray.find(p => p.term_no == term && p.isDueEntry && p.is_paid != 1)
            : this.state.paymentArray.find(p => p.term_no == term && !p.isDueEntry);
        const isDuePayment = !!(targetItem && targetItem.isDueEntry);

        // Net still owed on the target after its discount. When the discount fully covers it
        // (net <= 0), no money changes hands — it's a pure waiver, so payment mode isn't required.
        const targetNet = targetItem
            ? (targetItem.term_amount - (targetItem.term_discount || 0)) - (targetItem.paid_amount || 0)
            : 0;

        // Payment Mode is mandatory before recording any actual payment (skipped for full waivers).
        const paymentModeId = targetItem?.payment_mode_id;
        if (targetNet > 0 && (!paymentModeId || paymentModeId == "0" || paymentModeId == 0)) {
            window.alert("Payment Mode is mandatory. Please select a payment mode.");
            return;
        }

        this.state.paymentArray.forEach(item => {
            // Drop stale due entry for the original term when paying a regular term (will recreate if still partial)
            if (!isDuePayment && item.isDueEntry && item.dueFromTermNo == term) return;
            // Drop subsequent chained dues (by array position) when paying a due entry
            if (isDuePayment && item.isDueEntry && item.dueFromTermNo == targetItem?.dueFromTermNo) {
                const itemIdx = this.state.paymentArray.indexOf(item);
                const targetIdx = this.state.paymentArray.indexOf(targetItem);
                if (itemIdx > targetIdx) return;
            }

            // For due payment: only the first unpaid due is the target; already-paid dues are never modified
            const isTarget = item.term_no == term && (forDue ? (item.isDueEntry && item.is_paid != 1 && item === targetItem) : !item.isDueEntry);

            if (isTarget) {
                if (isDuePayment) {
                    // A due entry can also carry a discount: net payable = due amount - its discount.
                    const netDue = item.term_amount - (item.term_discount || 0);
                    const payAmount = (enteredAmount !== null && enteredAmount > 0)
                        ? Math.min(enteredAmount, netDue)
                        : netDue;
                    // payAmount can legitimately be 0 when the discount fully waives the due — settle it.
                    if (payAmount === 0 && netDue > 0) { amountAlert = true; array.push(item); return; }
                    const balance = netDue - payAmount;
                    // Clicking Pay Due always records this row as paid (is_paid = 1). term_amount is left
                    // unchanged; only paid_amount reflects what was paid. Any unpaid balance becomes a new due entry.
                    array.push({ ...item, is_paid: 1, paid_amount: payAmount, paid_date: item.paid_date || now });
                    if (balance > 0) {
                        newDueEntry = {
                            ...blankTerm(item.term_no, balance),
                            isDueEntry: true,
                            dueFromTermNo: item.dueFromTermNo,
                            payment_id: item.payment_id || null,
                            student_payment_log_id: null,
                        };
                    }
                } else {
                    if (!item.term_date && item.term_no != 0) { dateAlert = true; }
                    const alreadyPaid = item.paid_amount || 0;
                    // Net payable = term amount minus the per-term discount (the discount is waived,
                    // so the term is fully settled once the net amount is paid).
                    const netPayable = item.term_amount - (item.term_discount || 0);
                    const remaining = netPayable - alreadyPaid;
                    const payAmount = (enteredAmount !== null && enteredAmount > 0) ? Math.min(enteredAmount, remaining) : remaining;
                    // payAmount can legitimately be 0 when the discount fully waives the term — settle it.
                    if (payAmount === 0 && remaining > 0) { amountAlert = true; array.push(item); return; }
                    const totalPaid = alreadyPaid + payAmount;
                    const balance = netPayable - totalPaid;
                    // Clicking Make Payment always records this term as paid (is_paid = 1). term_amount is left
                    // unchanged; only paid_amount reflects what was paid. Any unpaid balance becomes a new due entry.
                    array.push({ ...item, is_paid: 1, paid_amount: totalPaid, paid_date: item.paid_date || now });
                    if (balance > 0) {
                        newDueEntry = {
                            ...blankTerm(parseInt(term), balance),
                            isDueEntry: true,
                            dueFromTermNo: parseInt(term),
                            payment_id: item.payment_id || null,
                            student_payment_log_id: null,
                        };
                    }
                }
            } else {
                array.push(item);
            }
        });

        // When due entry fully paid → mark original term paid only if ALL dues in the chain are now paid
        if (isDuePayment && !newDueEntry && targetItem) {
            const allDuesPaid = array.every(
                a => !(a.isDueEntry && a.dueFromTermNo == targetItem.dueFromTermNo) || a.is_paid == 1
            );
            if (allDuesPaid) {
                const origIdx = array.findIndex(a => a.term_no == targetItem.dueFromTermNo && !a.isDueEntry);
                if (origIdx >= 0) array[origIdx] = { ...array[origIdx], is_paid: 1 };
            }
        }

        if (newDueEntry) {
            // Find the LAST matching entry (not first) so the new due is inserted after the
            // just-paid record, not after an earlier already-paid due with the same term_no
            let insertAfter = -1;
            for (let idx = array.length - 1; idx >= 0; idx--) {
                if (array[idx].term_no == term && (isDuePayment ? array[idx].isDueEntry : !array[idx].isDueEntry)) {
                    insertAfter = idx;
                    break;
                }
            }
            if (insertAfter >= 0) array.splice(insertAfter + 1, 0, newDueEntry);
            else array.push(newDueEntry);
        }

        if (amountAlert) {
            window.alert("Amount must be greater than zero !!!");
        } else if (dateAlert) {
            window.alert("Please select the term date.");
        } else {
            const newPayInput = { ...this.state.payInputAmounts };
            delete newPayInput[payKey];
            this.setState({ paymentArray: array, payInputAmounts: newPayInput, pendingChanges: true });
        }
    };

    submitPaymentNow = (paymentArray) => {
        const regularFeeTerms = paymentArray
            .filter(t => !t.isDueEntry)
            .map(item => {
                const termAmount = parseInt(item.term_amount) || 0;
                const termDiscount = parseInt(item.term_discount) || 0;
                const netPayable = termAmount - termDiscount;
                const rawPaid = parseInt(item.paid_amount) || 0;
                // Legacy/full-pay rows may carry paid_amount 0 while is_paid is 1 — treat as fully paid
                // for the NET amount (term amount minus the per-term discount).
                const paidAmt = rawPaid > 0 ? rawPaid : (parseInt(item.is_paid) ? netPayable : 0);
                // Any recorded payment marks the term paid (is_paid = 1). term_amount is sent in full;
                // paid_amount carries the amount paid, and the unpaid balance lives in a separate due entry.
                return {
                    student_payment_log_id: item.student_payment_log_id || null,
                    is_paid: paidAmt > 0 ? 1 : 0,
                    term_amount: termAmount,
                    term_discount: termDiscount,
                    internal_amount: parseInt(item.internal_amount) || 0,
                    paid_amount: paidAmt,
                    term_date: item.term_date ? item.term_date.toString() : "",
                    term_no: parseInt(item.term_no),
                    paid_date: item.paid_date ? item.paid_date.toString() : Math.floor(Date.now() / 1000).toString(),
                    payment_mode_id: parseInt(item.payment_mode_id) || 0,
                    payment_id: item.payment_id || null,
                    comments: item.comments ? item.comments.toString() : "",
                };
            });

        const dueFeeTerms = paymentArray
            .filter(t => t.isDueEntry)
            .map(item => {
                const termAmount = parseInt(item.term_amount) || 0;
                const termDiscount = parseInt(item.term_discount) || 0;
                const netPayable = termAmount - termDiscount;
                const rawPaid = parseInt(item.paid_amount) || 0;
                // Legacy/full-pay rows may carry paid_amount 0 while is_paid is 1 — treat as fully paid for the NET.
                const paidAmt = rawPaid > 0 ? rawPaid : (parseInt(item.is_paid) ? netPayable : 0);
                // Any recorded payment marks the due paid (is_paid = 1); the unpaid balance is a separate due entry.
                return {
                    student_payment_log_id: item.student_payment_log_id || null,
                    is_paid: paidAmt > 0 ? 1 : 0,
                    term_amount: termAmount,
                    term_discount: termDiscount,
                    internal_amount: parseInt(item.internal_amount) || 0,
                    paid_amount: paidAmt,
                    term_date: item.term_date ? item.term_date.toString() : "",
                    term_no: parseInt(item.term_no),
                    paid_date: item.paid_date ? item.paid_date.toString() : Math.floor(Date.now() / 1000).toString(),
                    payment_mode_id: parseInt(item.payment_mode_id) || 0,
                    payment_id: item.payment_id || null,
                    comments: item.comments ? item.comments.toString() : "",
                    is_due_entry: 1,
                    due_from_term_no: parseInt(item.dueFromTermNo),
                };
            });

        const params = {
            student_id: parseInt(this.props.stateData.student_id),
            academic_year_id: parseInt(this.props.stateData.yearSearch),
            total_fee: parseInt(this.state.totalFee),
            discount: parseInt(this.state.discountFee) || 0,
            fee_amount: parseInt(this.state.feeAmount),
            no_of_terms: parseInt(this.state.termsValue),
            initial_amount: 0,
            fee_type_id: parseInt(this.state.feeType),
            fee_terms: [...regularFeeTerms, ...dueFeeTerms],
        };

        this.setState({ isSubmitting: true });
        this.submitTerms(params).catch(error => {
            this.setState({
                submitError: error.graphQLErrors?.map(x => x.message) || "Submission failed",
                isSubmitting: false,
            });
        });
    };

    updateTermAmount = (value, termNo, isDueEntry) => {
        const newAmount = parseInt(value) || 0;
        const changedIdx = this.state.paymentArray.findIndex(
            t => t.term_no == termNo && !!t.isDueEntry === !!isDueEntry
        );
        if (changedIdx < 0) return;

        const afterChange = this.state.paymentArray.map((t, i) =>
            i === changedIdx ? { ...t, term_amount: newAmount, termAmountError: "" } : t
        );

        // Redistribute only into unpaid regular terms that come AFTER the changed term.
        // Paid terms, due entries, terms with due-entry chains, and terms before the changed one are never touched.
        const feeAmount = parseInt(this.state.feeAmount) || 0;

        // A term is locked if paid, partially paid, OR has due entries (payments tracked via due-entry chain)
        const hasDueEntries = (t) => this.state.paymentArray.some(d => d.isDueEntry && d.dueFromTermNo == t.term_no);
        const isLocked = (t) => t.is_paid == 1 || (t.paid_amount || 0) > 0 || hasDueEntries(t);

        // Fixed amount = locked terms (any position) + unpaid terms before the changed term
        const fixedAmount = afterChange
            .filter((t, i) => !t.isDueEntry && i !== changedIdx && (isLocked(t) || i < changedIdx))
            .reduce((s, t) => s + (parseInt(t.term_amount) || 0), 0);

        const toDistribute = feeAmount - fixedAmount - newAmount;

        const subsequentUnpaid = afterChange
            .map((t, i) => ({ t, i }))
            .filter(({ t, i }) => !t.isDueEntry && i > changedIdx && !isLocked(t));

        if (subsequentUnpaid.length > 0 && toDistribute >= 0) {
            const perTerm = Math.floor(toDistribute / subsequentUnpaid.length);
            const remainder = toDistribute - perTerm * subsequentUnpaid.length;
            subsequentUnpaid.forEach(({ i }, idx) => {
                afterChange[i] = {
                    ...afterChange[i],
                    term_amount: idx === subsequentUnpaid.length - 1 ? perTerm + remainder : perTerm,
                    termAmountError: "",
                };
            });
        }

        this.setState({ paymentArray: afterChange, pendingChanges: true });
    };

    // Per-term discount: a waiver applied to a single row (a regular term OR a due entry). It reduces
    // only that row's net payable (amount - discount); the amount and every other row stay untouched.
    // Keyed by array index so chained due entries (same term_no) are addressed unambiguously.
    updateTermDiscount = (value, idx) => {
        const term = this.state.paymentArray[idx];
        if (!term) return;
        // Discount can never exceed the row amount, nor cut into what's already been paid.
        const maxDiscount = Math.max(0, (parseInt(term.term_amount) || 0) - (parseInt(term.paid_amount) || 0));
        const discount = Math.min(Math.max(0, parseInt(value) || 0), maxDiscount);
        const arr = this.state.paymentArray.map((t, i) =>
            i === idx ? { ...t, term_discount: discount } : t
        );
        this.setState({ paymentArray: arr, pendingChanges: true });
    };

    // "Others" (internal amount): a per-row value that is just stored and shown (no payable math).
    // Constraint: it must be LESS THAN the amount being paid for that term.
    payAmountForRow = (term) => {
        if (!term) return 0;
        const netPayable = (parseInt(term.term_amount) || 0) - (parseInt(term.term_discount) || 0);
        const remaining = netPayable - (parseInt(term.paid_amount) || 0);
        const key = term.isDueEntry ? `${term.term_no}_due` : term.term_no;
        const entered = this.state.payInputAmounts[key];
        return (entered !== undefined && entered !== "") ? (parseInt(entered) || 0) : remaining;
    };

    updateInternalAmount = (value, idx) => {
        const term = this.state.paymentArray[idx];
        if (!term) return;
        // Must be strictly less than the pay amount.
        const maxOthers = Math.max(0, this.payAmountForRow(term) - 1);
        const amt = Math.min(Math.max(0, parseInt(value) || 0), maxOthers);
        const arr = this.state.paymentArray.map((t, i) =>
            i === idx ? { ...t, internal_amount: amt } : t
        );
        this.setState({ paymentArray: arr, pendingChanges: true });
    };

    handleFullPayment = () => {
        const { fullPaymentMode, paymentArray } = this.state;
        if (!fullPaymentMode || fullPaymentMode == "0") {
            window.alert("Payment Mode is mandatory. Please select a payment mode for full payment.");
            return;
        }
        const now = Math.floor(Date.now() / 1000).toString();
        const newArray = paymentArray.map(item => {
            if (item.is_paid == 1) return item;
            // Both regular terms and due entries pay their net (amount - discount).
            const net = item.term_amount - (item.term_discount || 0);
            return {
                ...item,
                is_paid: 1,
                paid_amount: net,
                payment_mode_id: parseInt(fullPaymentMode),
                paid_date: item.paid_date || now,
            };
        });
        this.setState({ paymentArray: newArray, pendingChanges: true });
    };

    paymentModeAndComments = (e, term, forDue = false) => {
        const { name, value } = e.target;
        const newArr = this.state.paymentArray.map(item =>
            item.term_no == term && (forDue ? item.isDueEntry : !item.isDueEntry)
                ? { ...item, [name === "comments" ? "comments" : "payment_mode_id"]: value }
                : item
        );
        this.setState({ paymentArray: newArr });
    };

    getLogoDataUrl = async (logoFile) => {
        if (!logoFile) return "";
        const url = `https://support.rizee.in/files/${logoFile}`;
        try {
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) return "";
            const blob = await res.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => resolve("");
                reader.readAsDataURL(blob);
            });
        } catch {
            return "";
        }
    };

    viewPDF = async (items) => {
        const itemArr = Array.isArray(items) ? items : [items];
        console.log("viewPDF batch:", itemArr.map(t => ({ term_no: t.term_no, paid_amount: t.paid_amount, paid_date: t.paid_date })));
        const i = itemArr[0];

        const { studentName, getPaymentModes, stateData } = this.props;
        const { orgProfile } = this.state;
        const orgName = orgProfile.organisation_name || "";
        const logoFile = orgProfile.logo || "";
        const complete_address = orgProfile.complete_address || "";
        const paymentMode = getPaymentModes.find(p => p.id === i.payment_mode_id);
        const paymentDetails = paymentMode?.payment_mode || "";
        const feeTypes = this.props.getFeeTypes?.getFeeTypes || [];
        const feeType = feeTypes.find(f => f.id == this.state.feeType)?.fee_type_name || '';

        const logoDataUrl = `https://support.rizee.in/files/${logoFile}`;
        const receiptDate = i.paid_date ? moment.unix(parseInt(i.paid_date)).format('DD/MMM/YYYY') : '—';
        const academicYear = stateData.academic_year_text || stateData.academic_year_name || stateData.year_name || '';
        const classAdm = [stateData.class_name, stateData.admission_no].filter(Boolean).join(' / Adm No: ');
        const totalPaidRaw = itemArr.reduce((s, t) => s + (t.paid_amount || 0), 0);
        const totalPaid = totalPaidRaw.toLocaleString('en-IN');
        // Discount applied to the term(s) on this receipt.
        const receiptDiscount = itemArr.reduce((s, t) => s + (parseInt(t.term_discount) || 0), 0);

        const totalPaidAllTerms = this.state.paymentArray.reduce((s, t) => s + (parseInt(t.paid_amount) || 0), 0);
        const totalDiscountAllTerms = this.state.paymentArray.reduce((s, t) => s + (parseInt(t.term_discount) || 0), 0);
        // Outstanding = committed amount minus all waivers and all payments.
        const overallDue = Math.max(0, (parseInt(this.state.feeAmount) || 0) - totalDiscountAllTerms - totalPaidAllTerms);

        const hideFeeType = !!this.state.showFeeTypeInPdf[i.term_no];

        const dataRows = [
            academicYear ? ["Academic Year", academicYear] : null,
            ["Student Name", studentName || '—'],
            stateData.parent_name ? ["Parent Name", stateData.parent_name] : null,
            ["Class / Adm No", classAdm || '—'],
            !hideFeeType ? ["Fee Type", feeType || '—'] : null,
            receiptDiscount > 0 ? ["Discount", `&#x20B9;${receiptDiscount.toLocaleString('en-IN')}`] : null,
            ["Total Paid", `&#x20B9;${totalPaid} (${paymentDetails})`],
            overallDue > 0 ? ["Balance amount", `&#x20B9;${overallDue.toLocaleString('en-IN')}`] : null,
        ].filter(Boolean);

        const rowsHtml = dataRows.map(([label, value]) => `
            <tr>
                <td style="padding:6px 0;font-size:13px;color:#555;width:220px">${label}</td>
                <td style="padding:6px 0;font-size:13px;color:#555;width:20px;text-align:center">:</td>
                <td style="padding:6px 0;font-size:13px;font-weight:700;color:#111">${value}</td>
            </tr>`).join('');

        const win = window.open('', '_blank', 'width=800,height=700');
        win.document.write(`<html><head><title>Fee Receipt</title><style>
            *{margin:0;padding:0;box-sizing:border-box}
            body{font-family:Arial,sans-serif;padding:24px;color:#111;font-size:12px}
            .receipt-wrapper{width:700px;margin:0 auto;border:1.5px solid #333}
            .receipt-header{display:flex;align-items:center;gap:14px;padding:14px 18px 10px;border-bottom:1.5px solid #333}
            .hdr-logo{height:56px;width:auto;object-fit:contain;flex-shrink:0}
            .hdr-info{flex:1;text-align:center}
            .org-name{font-size:18px;font-weight:700;color:#111;margin-bottom:2px}
            .org-addr{font-size:11px;color:#555;line-height:1.5;margin:0 auto;max-width:500px}
            .receipt-title{display:flex;align-items:center;padding:10px 18px;border-bottom:1.5px solid #333}
            .rt-side{flex:1;font-size:11px;font-weight:400;color:#333;line-height:1.5}
            .rt-main{font-size:16px;font-weight:700;text-align:center}
            .receipt-body{padding:14px 18px}
            .receipt-footer{border-top:1.5px solid #333;padding:10px 18px}
            @media print{html,body{height:auto!important}body{padding:2px}@page{size:auto;margin:3mm}}
        </style></head><body>
            <div class="receipt-wrapper">
                <div class="receipt-header">
                    ${logoDataUrl ? `<img class="hdr-logo" src="${logoDataUrl}" />` : ''}
                    <div class="hdr-info">
                        <div class="org-name">${orgName}</div>
                        <div class="org-addr">${complete_address}</div>
                    </div>
                </div>
                <div class="receipt-title">
                    <div class="rt-side" style="text-align:left">Receipt No: <strong>${i.student_payment_log_id || '—'}</strong><br/>Adm No: <strong>${stateData.admission_no || '—'}</strong></div>
                    <div class="rt-main">Fee Receipt</div>
                    <div class="rt-side" style="text-align:right">Date: <strong>${receiptDate}</strong></div>
                </div>
                <div class="receipt-body">
                    <table style="width:100%;border-collapse:collapse">
                        <tbody>${rowsHtml}</tbody>
                    </table>
                </div>
                <div class="receipt-footer">
                    <div style="text-align:center;font-size:10px;color:#555;line-height:1.3">Note: Once a payment is made, it cannot be refunded. Thank You</div>
                    <div style="text-align:right;font-size:11px;font-weight:700;color:#333;margin-top:16px">School Stamp &amp; Sign</div>
                </div>
            </div>
            <script>window.print();window.close();<\/script>
        </body></html>`);
        win.document.close();
        win.focus();
    };

    // Build a single consolidated statement listing every payment made for the selected
    // fee type (paid terms + paid due entries) so the user can download the whole history at once.
    downloadAllPayments = () => {
        const { studentName, getPaymentModes, stateData } = this.props;
        const { orgProfile } = this.state;
        const orgName = orgProfile.organisation_name || "";
        const logoFile = orgProfile.logo || "";
        const complete_address = orgProfile.complete_address || "";
        const feeTypes = this.props.getFeeTypes?.getFeeTypes || [];
        const feeType = feeTypes.find(f => f.id == this.state.feeType)?.fee_type_name || '';
        const logoDataUrl = logoFile ? `https://support.rizee.in/files/${logoFile}` : '';

        const academicYear = stateData.academic_year_text || stateData.academic_year_name || stateData.year_name || '';
        const classAdm = [stateData.class_name, stateData.admission_no].filter(Boolean).join(' / Adm No: ');

        const paidEntries = this.state.paymentArray
            .map(t => ({ ...t, _paidAmt: t.paid_amount || (t.is_paid == 1 ? (t.term_amount - (parseInt(t.term_discount) || 0)) : 0), _discount: parseInt(t.term_discount) || 0 }))
            // Include rows with a payment OR a discount (a fully-waived term has ₹0 paid but a discount).
            .filter(t => t._paidAmt > 0 || t._discount > 0)
            .sort((a, b) => (parseInt(a.paid_date) || 0) - (parseInt(b.paid_date) || 0));

        if (paidEntries.length === 0) {
            window.alert("No payments found to download.");
            return;
        }

        const totalPaid = paidEntries.reduce((s, t) => s + t._paidAmt, 0);
        const totalDiscount = paidEntries.reduce((s, t) => s + t._discount, 0);
        const totalFee = parseInt(this.state.totalFee) || 0;
        // Overall fee-level discount (applied to Total Fee), separate from the per-term discounts.
        const feeDiscount = parseInt(this.state.discountFee) || 0;
        const feeAmount = parseInt(this.state.feeAmount) || 0;
        const overallDue = Math.max(0, feeAmount - totalDiscount - totalPaid);

        const rowsHtml = paidEntries.map((t, idx) => {
            const mode = getPaymentModes.find(p => p.id == t.payment_mode_id)?.payment_mode || '—';
            const label = t.isDueEntry ? `Due (Term ${t.dueFromTermNo})` : `Term ${t.term_no}`;
            const date = t.paid_date ? moment.unix(parseInt(t.paid_date)).format('DD/MMM/YYYY') : '—';
            const receiptNo = t.student_payment_log_id || t.payment_id || '—';
            return `<tr>
                <td style="padding:7px 10px;border:1px solid #e5e7eb;text-align:center">${idx + 1}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb">${receiptNo}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb">${label}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb">${date}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb">${mode}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb;text-align:right">${t._discount > 0 ? '&#x20B9;' + t._discount.toLocaleString('en-IN') : '—'}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb;text-align:right;font-weight:700">&#x20B9;${t._paidAmt.toLocaleString('en-IN')}</td>
            </tr>`;
        }).join('');

        const infoRows = [
            academicYear ? ["Academic Year", academicYear] : null,
            ["Student Name", studentName || '—'],
            stateData.parent_name ? ["Parent Name", stateData.parent_name] : null,
            ["Class / Adm No", classAdm || '—'],
            feeType ? ["Fee Type", feeType] : null,
        ].filter(Boolean).map(([label, value]) => `
            <tr>
                <td style="padding:4px 0;font-size:12px;color:#555;width:160px">${label}</td>
                <td style="padding:4px 0;font-size:12px;color:#555;width:14px;text-align:center">:</td>
                <td style="padding:4px 0;font-size:12px;font-weight:700;color:#111">${value}</td>
            </tr>`).join('');

        const win = window.open('', '_blank', 'width=900,height=750');
        win.document.write(`<html><head><title>Payment History</title><style>
            *{margin:0;padding:0;box-sizing:border-box}
            body{font-family:Arial,sans-serif;padding:24px;color:#111;font-size:12px}
            .receipt-wrapper{width:780px;margin:0 auto;border:1.5px solid #333}
            .receipt-header{display:flex;align-items:flex-start;justify-content:space-between;padding:14px 18px 10px;border-bottom:1.5px solid #333}
            .hdr-left{display:flex;align-items:flex-start;gap:14px;flex:1}
            .hdr-left img{width:auto;height:60px;object-fit:contain;flex-shrink:0}
            .org-name{font-size:18px;font-weight:700;color:#111;margin-bottom:2px}
            .org-addr{font-size:11px;color:#555;line-height:1.5}
            .receipt-title{text-align:center;font-size:16px;font-weight:700;padding:10px 0;border-bottom:1.5px solid #333}
            .receipt-body{padding:14px 18px}
            .history-table{width:100%;border-collapse:collapse;margin-top:12px;font-size:12px}
            .history-table th{padding:8px 10px;border:1px solid #333;background:#f3f4f6;font-size:12px;text-align:left}
            .totals{margin-top:14px;display:flex;flex-wrap:wrap;justify-content:flex-end;gap:12px 30px;font-size:13px}
            .receipt-footer{border-top:1.5px solid #333;padding:10px 18px}
            @media print{html,body{height:auto!important}body{padding:2px}@page{size:auto;margin:5mm}}
        </style></head><body>
            <div class="receipt-wrapper">
                <div class="receipt-header">
                    <div class="hdr-left">
                        ${logoDataUrl ? `<img src="${logoDataUrl}" />` : ''}
                        <div>
                            <div class="org-name">${orgName}</div>
                            <div class="org-addr">${complete_address}</div>
                        </div>
                    </div>
                    <div style="text-align:right;font-size:10px;color:#333;min-width:160px;line-height:1.9">
                        <div>Date: <strong>${moment().format('DD/MMM/YYYY')}</strong></div>
                        <div>Total Receipts: <strong>${paidEntries.length}</strong></div>
                    </div>
                </div>
                <div class="receipt-title">Payment History</div>
                <div class="receipt-body">
                    <table style="width:100%;border-collapse:collapse"><tbody>${infoRows}</tbody></table>
                    <table class="history-table">
                        <thead>
                            <tr>
                                <th style="text-align:center;width:40px">#</th>
                                <th>Receipt No</th>
                                <th>Term</th>
                                <th>Paid Date</th>
                                <th>Payment Mode</th>
                                <th style="text-align:right">Discount</th>
                                <th style="text-align:right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>${rowsHtml}</tbody>
                    </table>
                    <div class="totals">
                        ${totalFee > 0 ? `<div>Total Fee: <strong style="color:#111">&#x20B9;${totalFee.toLocaleString('en-IN')}</strong></div>` : ''}
                        ${feeDiscount > 0 ? `<div>Fee Discount: <strong style="color:#1d4ed8">&#x20B9;${feeDiscount.toLocaleString('en-IN')}</strong></div>` : ''}
                        ${totalDiscount > 0 ? `<div>Term Discount: <strong style="color:#1d4ed8">&#x20B9;${totalDiscount.toLocaleString('en-IN')}</strong></div>` : ''}
                        <div>Total Paid: <strong style="color:#15803d">&#x20B9;${totalPaid.toLocaleString('en-IN')}</strong></div>
                        ${overallDue > 0 ? `<div>Balance Due: <strong style="color:#dc2626">&#x20B9;${overallDue.toLocaleString('en-IN')}</strong></div>` : ''}
                    </div>
                </div>
                <div class="receipt-footer">
                    <div style="text-align:center;font-size:10px;color:#555;margin-bottom:2px">Note: Once a payment is made, it cannot be refunded.</div>
                    <div style="text-align:center;font-size:10px;color:#555">Thank You</div>
                    <div style="text-align:right;font-size:11px;font-weight:700;color:#333;margin-top:28px">School Stamp &amp; Sign</div>
                </div>
            </div>
            <script>window.print();window.close();<\/script>
        </body></html>`);
        win.document.close();
        win.focus();
    };

    handleFormSubmit = async () => {
        if (this.state.feeType == 0) {
            this.setState({ feeTypeError: "Please select Fee type" });
            return;
        }
        if (+this.state.discountFee > +this.state.totalFee) {
            this.setState({ discountFeeError: "Discount cannot be greater than total fee" });
            return;
        }
        if (this.state.feeAmount === "" || this.state.feeAmount === 0) {
            this.setState({ feeAmountError: "Please enter Fee Amount" });
            return;
        }
        if (!this.state.termsValue) {
            this.setState({ termsValueError: "Please enter number of terms" });
            return;
        }

        // Validate term amounts and dates
        let hasError = false;
        const validated = this.state.paymentArray.map(item => {
            let it = { ...item };
            if (!it.isDueEntry && (!it.term_amount || it.term_amount == 0)) {
                it = { ...it, termAmountError: "Enter valid amount" };
                hasError = true;
            }
            if (!it.isDueEntry && it.is_paid != 1 && !it.term_date) {
                it = { ...it, dateError: "Enter valid date" };
                hasError = true;
            }
            return it;
        });
        this.setState({ paymentArray: validated });
        if (hasError) return;

        // term_amount is always the original full term amount; due entries are subsets of their
        // parent term's amount, so only regular terms are summed to avoid double-counting.
        const termsTotal = validated.reduce((s, t) => {
            if (t.isDueEntry) return s;
            return s + (parseInt(t.term_amount) || 0);
        }, 0);
        if (parseInt(this.state.feeAmount) !== termsTotal) {
            const diff = parseInt(this.state.feeAmount) - termsTotal;
            // Guard: only auto-fix if every regular term_no (1..termsValue) is present.
            // If a term is missing (data corrupted by a prior buggy submission), skip the
            // auto-fix rather than silently inflating another term's amount.
            const numTerms = parseInt(this.state.termsValue) || 0;
            const presentTermNos = new Set(validated.filter(t => !t.isDueEntry).map(t => parseInt(t.term_no)));
            const allRegularPresent = numTerms > 0 && !Array.from({ length: numTerms }, (_, i) => i + 1).some(n => !presentTermNos.has(n));
            if (allRegularPresent) {
                // Auto-fix: apply the difference to the unpaid regular term with the highest amount
                let maxIdx = -1, maxAmt = -Infinity;
                validated.forEach((t, idx) => {
                    if (!t.isDueEntry && t.is_paid != 1 && (t.paid_amount || 0) === 0) {
                        const amt = parseInt(t.term_amount) || 0;
                        if (amt > maxAmt) { maxAmt = amt; maxIdx = idx; }
                    }
                });
                if (maxIdx < 0) {
                    this.setState({ submitError: `Terms total ₹${termsTotal} does not match committed amount ₹${this.state.feeAmount}. Please adjust term amounts.` });
                    return;
                }
                validated[maxIdx] = { ...validated[maxIdx], term_amount: maxAmt + diff };
                this.setState({ paymentArray: validated });
            }
        }

        // Auto-apply any due entries that have a payment mode selected,
        // so the user doesn't need to click "Pay Due" separately before submitting.
        // Process in term_no order so earlier dues in each chain are handled first.
        let finalArray = [...validated];
        const { payInputAmounts } = this.state;

        // Skip dues already handled by paymentCheckBox (paid_amount > 0 means paymentCheckBox
        // already set the correct amount — re-processing would overwrite it with term_amount).
        const dueOrder = finalArray
            .map((item, idx) => ({ item, idx }))
            .filter(({ item }) => item.isDueEntry && item.is_paid != 1 && (item.paid_amount || 0) === 0 && item.payment_mode_id && item.payment_mode_id != 0)
            .sort((a, b) => a.item.term_no - b.item.term_no);

        for (const { item } of dueOrder) {
            const idx = finalArray.findIndex(d => d.term_no == item.term_no && d.isDueEntry);
            if (idx < 0 || finalArray[idx].is_paid == 1) continue;

            const current = finalArray[idx];
            const k = `${current.term_no}_due`;
            // Net payable for the due = due amount minus its own discount.
            const netDue = current.term_amount - (current.term_discount || 0);
            const entered = payInputAmounts[k] !== undefined ? parseInt(payInputAmounts[k]) || 0 : 0;
            const payAmount = entered > 0 ? Math.min(entered, netDue) : netDue;
            // payAmount 0 with netDue 0 is a full waiver — still settle it; only skip genuinely negative.
            if (payAmount < 0 || (payAmount === 0 && netDue > 0)) continue;

            const balance = netDue - payAmount;
            // Auto-apply records the due as paid (is_paid = 1); term_amount is left unchanged,
            // paid_amount carries what was paid.
            finalArray[idx] = { ...current, is_paid: 1, paid_amount: payAmount };

            // If only partially paid via auto-apply, split the remainder into the next due entry
            if (balance > 0) {
                const nextDue = {
                    ...blankTerm(current.term_no, balance),
                    isDueEntry: true,
                    dueFromTermNo: current.dueFromTermNo,
                    payment_id: current.payment_id || null,
                    student_payment_log_id: null,
                };
                const insertAfter = finalArray.findIndex(d => d.term_no == current.term_no && d.isDueEntry);
                if (insertAfter >= 0) finalArray.splice(insertAfter + 1, 0, nextDue);
                else finalArray.push(nextDue);
            }

            // Re-affirm the original term as paid once ALL dues in the chain are settled
            if (balance === 0 && current.dueFromTermNo) {
                const allDuesPaid = finalArray.every(
                    a => !(a.isDueEntry && a.dueFromTermNo == current.dueFromTermNo) || a.is_paid == 1
                );
                if (allDuesPaid) {
                    const origIdx = finalArray.findIndex(a => a.term_no == current.dueFromTermNo && !a.isDueEntry);
                    if (origIdx >= 0) finalArray[origIdx] = { ...finalArray[origIdx], is_paid: 1 };
                }
            }
        }

        this.setState({ paymentArray: finalArray }, () => {
            this.submitPaymentNow(finalArray);
        });
    };

    refreshFeeData = async (keepStep = false) => {
        const feeTypeId = parseInt(this.state.feeType);
        if (!feeTypeId) return;
        try {
            const { data } = await this.props.client.query({
                query: GET_STUDENT_FEE_BY_TYPE,
                variables: {
                    student_id: parseInt(this.props.stateData.student_id),
                    academic_year_id: parseInt(this.props.stateData.yearSearch),
                    fee_type_id: feeTypeId,
                },
                fetchPolicy: "network-only",
            });
            const res = data?.getStudentFeeByType;
            if (!res) return;
            const totalFee = res.total_fee ?? 0;
            const discountFee = res.discount ?? 0;
            const feeAmount = totalFee - discountFee;
            const _tc2 = {};
            const terms = (res.terms || []).map(t => {
                const isExplicitDue = !!t.is_due_entry || t.due_from_term_no != null;
                const tn = t.term_no;
                let isDueEntry = isExplicitDue;
                if (!isExplicitDue) {
                    const occ = _tc2[tn] || 0;
                    _tc2[tn] = occ + 1;
                    isDueEntry = occ > 0;
                }
                const dueFromTermNo = t.due_from_term_no != null ? t.due_from_term_no : isDueEntry ? tn : null;
                return { ...t, paid_amount: t.paid_amount || 0, isDueEntry, dueFromTermNo, termAmountError: "", dateError: "" };
            });
            const paidTermsCount = terms.filter(t => t.is_paid && !t.isDueEntry).length;
            const regularTermCount = terms.filter(t => !t.isDueEntry).length;
            this.setState({
                totalFee, discountFee, feeAmount,
                termsValue: res.no_of_terms ?? "",
                paymentArray: terms,
                paidTermsCount,
                defaultArray: terms,
                isExistingFee: true,
                termsConfigured: res.no_of_terms !== null,
                isSubmitting: false,
                loadingSpinner: 0,
                currentStep: keepStep ? 5 : 1,
                submitError: "",
                pendingChanges: false,
            });
            if (this.props.onRefresh) this.props.onRefresh();
        } catch (err) {
            console.error("refreshFeeData error:", err);
            this.setState({ isSubmitting: false, loadingSpinner: 0 });
        }
    };

    // Last gate before updateTerms — the payment / concession / term write — leaves the
    // client.
    submitTerms = async params => {
        if (!canEdit(FEE_STRUCTURE_MODULE)) return;

        await this.props.updateTerms({
            variables: { params },
            update: (store, { data }) => {
                if (data.updateTerms) {
                    this.setState({ loadingSpinner: 0, currentStep: 5, isSubmitting: false }, () => {
                        this.refreshFeeData(true);
                    });
                }
            },
        });
    };

    // Return to the editing view with the just-refreshed fee data still loaded so the
    // user can record another payment. Does NOT re-submit or reset — the data is already
    // saved, and the modal stays open until the user explicitly clicks Close.
    handleMakeAnotherPayment = () => {
        this.setState({ currentStep: 1, submitError: "", pendingChanges: false });
    };

    SetpageLoad = () => {
        this.setState({ currentStep: 1, submitError: "", loadingSpinner: 0 });
        this.props.onHide();
        if (this.props.onRefresh) this.props.onRefresh();
    };

    render() {
        console.log("data", this.props.stateData);

        const S = {
            label: { fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px", display: "block" },
            input: { borderRadius: 8, border: "1.5px solid #e5e7eb", padding: "8px 12px", fontSize: 13, color: "#1f2937", background: "#fff", width: "100%", outline: "none", transition: "border 0.2s" },
            inputDisabled: { borderRadius: 8, border: "1.5px solid #f3f4f6", padding: "8px 12px", fontSize: 13, color: "#9ca3af", background: "#f9fafb", width: "100%" },
            select: { borderRadius: 8, border: "1.5px solid #e5e7eb", padding: "8px 12px", fontSize: 13, color: "#1f2937", background: "#fff", width: "100%", height: 38, outline: "none" },
            errorText: { color: "#ef4444", fontSize: 11, marginTop: 3 },
            termCardPaid: { background: "#fff", border: "1px solid #d1fae5", borderRadius: 14, padding: "16px 18px", marginBottom: 10, borderLeft: "4px solid #10b981", boxShadow: "0 1px 4px rgba(16,185,129,0.07)" },
            termCardNext: { background: "#fff", border: "1px solid #c7d2fe", borderRadius: 14, padding: "16px 18px", marginBottom: 10, borderLeft: "4px solid #6366f1", boxShadow: "0 1px 4px rgba(99,102,241,0.08)" },
            termCardPartial: { background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 14, padding: "16px 18px", marginBottom: 10, borderLeft: "4px solid #f59e0b", boxShadow: "0 1px 4px rgba(245,158,11,0.08)" },
            termCardDue: { background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 14, padding: "16px 18px", marginBottom: 10, borderLeft: "4px solid #f97316", boxShadow: "0 1px 4px rgba(249,115,22,0.10)" },
            termCardPending: { background: "#fafafa", border: "1px solid #f3f4f6", borderRadius: 14, padding: "16px 18px", marginBottom: 10, borderLeft: "4px solid #d1d5db" },
            termLabel: { fontSize: 12, fontWeight: 700, color: "#374151", letterSpacing: "0.3px" },
            paidBadge: { background: "#dcfce7", color: "#15803d", borderRadius: 20, padding: "2px 12px", fontSize: 11, fontWeight: 700 },
            partialBadge: { background: "#fef3c7", color: "#b45309", borderRadius: 20, padding: "2px 12px", fontSize: 11, fontWeight: 700 },
            dueBadge: { background: "#ffedd5", color: "#c2410c", borderRadius: 20, padding: "2px 12px", fontSize: 11, fontWeight: 700 },
            nextBadge: { background: "#ede9fe", color: "#6d28d9", borderRadius: 20, padding: "2px 12px", fontSize: 11, fontWeight: 700 },
            makePayBtn: { background: "linear-gradient(135deg, #6366f1, #8b5cf6)", border: "none", borderRadius: 8, padding: "7px 18px", fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer" },
            downloadBtn: { background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#15803d", cursor: "pointer", marginTop: 6 },
            submitBtn: { background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", border: "none", borderRadius: 10, padding: "10px 32px", fontWeight: 700, fontSize: 14, color: "#fff", boxShadow: "0 4px 14px rgba(99,102,241,0.35)", cursor: "pointer" },
            submitBtnDisabled: { background: "#e5e7eb", border: "none", borderRadius: 10, padding: "10px 32px", fontWeight: 700, fontSize: 14, color: "#9ca3af", cursor: "not-allowed" },
            summaryPill: { display: "flex", flexDirection: "column", alignItems: "center", background: "#f8fafc", borderRadius: 10, padding: "10px 18px", border: "1px solid #e5e7eb", minWidth: 100 },
        };

        const hasTermsData = this.state.isExistingFee && this.state.termsConfigured && this.state.paymentArray.length > 0;
        const allPaid = this.state.paymentArray.length > 0 && this.state.paymentArray.every(t => t.is_paid == 1);
        // As long as not a single payment has been recorded, Total Fee and Discount stay
        // editable (only No. of Terms is locked once terms are configured). Once any term/due
        // is even partially paid, the committed total and discount are frozen.
        const hasAnyPayment = this.state.paymentArray.some(t => t.is_paid == 1 || (t.paid_amount || 0) > 0);
        const totalDisabled = this.state.feeType == "1" || hasAnyPayment;
        const discountDisabled = hasAnyPayment;
        const termsDisabled = hasTermsData || this.state.feeAmount === "" || this.state.feeAmount === 0;

        const paidCount = this.state.paymentArray.filter(p => {
            if (p.isDueEntry) return false;
            const hasPendingDue = this.state.paymentArray.some(
                d => d.isDueEntry && d.dueFromTermNo == p.term_no && d.is_paid != 1
            );
            if (hasPendingDue) return false;
            if (p.is_paid == 1) return true;
            // partially paid — counts as settled once its due entry is paid
            if ((p.paid_amount || 0) > 0 && p.is_paid != 1) {
                const due = this.state.paymentArray.find(d => d.isDueEntry && d.dueFromTermNo == p.term_no);
                return due && due.is_paid == 1;
            }
            return false;
        }).length;
        const totalRegular = this.state.paymentArray.filter(p => !p.isDueEntry).length;
        const partialCount = this.state.paymentArray.filter(p => !p.isDueEntry && p.is_paid != 1 && (p.paid_amount || 0) > 0).length;
        const dueCount = this.state.paymentArray.filter(p => p.isDueEntry && p.is_paid != 1).length;

        return (
            <Modal {...this.props} size="xl" aria-labelledby="fee-modal" dialogClassName="fee-modal-dialog">
                <style>{`
                    @media (min-width: 768px) and (max-width: 1024px) {
                        .fee-modal-dialog { margin: 1.75rem auto; display: flex; flex-direction: column; }
                        .fee-modal-dialog .modal-content {
                            height: 80vh;
                            display: flex;
                            flex-direction: column;
                            overflow: hidden;
                        }
                        .fee-modal-dialog .modal-header { flex-shrink: 0; }
                        .fee-modal-dialog .modal-body   { flex: 1; min-height: 0; overflow-y: auto; }
                        .fee-modal-dialog .modal-footer { flex-shrink: 0; }
                    }
                `}</style>
                {/* ── Header ── */}
                <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", border: "none", padding: "18px 24px" }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "rgba(255,255,255,0.55)" }} />
                            <Modal.Title style={{ color: "#fff", fontWeight: 700, fontSize: 16, margin: 0 }}>Update Fees</Modal.Title>
                        </div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            {this.props.studentName && (
                                <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", borderRadius: 20, padding: "2px 12px", fontSize: 12, fontWeight: 600 }}>
                                    {this.props.studentName}
                                </span>
                            )}
                            {this.props.stateData?.roll_no && (
                                <span style={{ background: "rgba(255,255,255,0.13)", color: "rgba(255,255,255,0.85)", borderRadius: 20, padding: "2px 12px", fontSize: 12 }}>
                                    Adm: {this.props.stateData.roll_no}
                                </span>
                            )}
                            {this.props.stateData?.class_name && (
                                <span style={{ background: "rgba(255,255,255,0.13)", color: "rgba(255,255,255,0.85)", borderRadius: 20, padding: "2px 12px", fontSize: 12 }}>
                                    {this.props.stateData.class_name}
                                </span>
                            )}
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body style={{ padding: "20px 24px", background: "#f8fafc" }}>
                    {/* ── Fee Config ── */}
                    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "18px 20px", marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 14 }}>Fee Configuration</div>
                        <Row>
                            <Col lg={3} md={6} sm={12} className="mb-3">
                                <label style={S.label}>Fee Type <span style={{ color: "#ef4444" }}>*</span></label>
                                <select name="feeType" onChange={e => this.handleFeeFunction(e)} value={this.state.feeType} style={S.select} disabled={this.props.getFeeTypes?.loading}>
                                    <option value="">{this.props.getFeeTypes?.loading ? "Loading..." : "-- Select --"}</option>
                                    {(this.props.getFeeTypes?.getFeeTypes || []).map(item => (
                                        <option key={item.id} value={item.id}>{item.fee_type_name}</option>
                                    ))}
                                </select>
                                {this.state.feeTypeError && <div style={S.errorText}>{this.state.feeTypeError}</div>}
                            </Col>
                            <Col lg={3} md={6} sm={12} className="mb-3">
                                <label style={S.label}>Total Fee</label>
                                <input type="text" name="totalFee" value={this.state.totalFee} onChange={e => this.handleFeeFunction(e)} onKeyPress={this.onKeyPress} autoComplete="off" disabled={totalDisabled} style={totalDisabled ? S.inputDisabled : S.input} />
                                {this.state.totalFeeError && <div style={S.errorText}>{this.state.totalFeeError}</div>}
                            </Col>
                            <Col lg={3} md={6} sm={12} className="mb-3">
                                <label style={S.label}>Discount</label>
                                <input type="text" name="discountFee" value={this.state.discountFee || ""} onChange={e => this.handleFeeFunction(e)} onKeyPress={this.onKeyPress} autoComplete="off" disabled={discountDisabled} style={discountDisabled ? S.inputDisabled : S.input} />
                                {this.state.discountFeeError && <div style={S.errorText}>{this.state.discountFeeError}</div>}
                            </Col>
                            <Col lg={3} md={6} sm={12} className="mb-3">
                                <label style={S.label}>No. of Terms</label>
                                <input type="text" name="terms" maxLength={1} value={this.state.termsValue} onChange={e => this.handleFeeFunction(e)} onKeyPress={this.onKeyPress} autoComplete="off" disabled={termsDisabled} style={termsDisabled ? S.inputDisabled : S.input} />
                                {this.state.termsValueError && <div style={S.errorText}>{this.state.termsValueError}</div>}
                            </Col>
                        </Row>

                        {/* Committed / Paid / Pending pills */}
                        {(() => {
                            const totalPaid = this.state.paymentArray.reduce((s, t) => s + (parseInt(t.paid_amount) || 0), 0);
                            // Per-term discounts are waivers — they reduce what's collectable, so subtract
                            // them alongside paid amounts when computing the outstanding balance. Both regular
                            // terms and due entries can carry a discount.
                            const totalTermDiscount = this.state.paymentArray.reduce((s, t) => s + (parseInt(t.term_discount) || 0), 0);
                            const pending = Math.max(0, (this.state.feeAmount || 0) - totalTermDiscount - totalPaid);
                            return (
                                <div style={{ marginTop: 8, display: "flex", gap: 12, flexWrap: "wrap" }}>
                                    <div style={S.summaryPill}>
                                        <span style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px" }}>Committed Amount</span>
                                        <span style={{ fontSize: 18, fontWeight: 700, color: "#1f2937", marginTop: 2 }}>₹{this.state.feeAmount || 0}</span>
                                    </div>
                                    {totalTermDiscount > 0 && (
                                        <div style={{ ...S.summaryPill, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
                                            <span style={{ fontSize: 10, fontWeight: 600, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.5px" }}>Term Discounts</span>
                                            <span style={{ fontSize: 18, fontWeight: 700, color: "#1d4ed8", marginTop: 2 }}>₹{totalTermDiscount}</span>
                                        </div>
                                    )}
                                    <div style={{ ...S.summaryPill, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                                        <span style={{ fontSize: 10, fontWeight: 600, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.5px" }}>Paid Amount</span>
                                        <span style={{ fontSize: 18, fontWeight: 700, color: "#15803d", marginTop: 2 }}>₹{totalPaid}</span>
                                    </div>
                                    <div style={{ ...S.summaryPill, background: "#fef2f2", border: "1px solid #fecaca" }}>
                                        <span style={{ fontSize: 10, fontWeight: 600, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.5px" }}>Pending Amount</span>
                                        <span style={{ fontSize: 18, fontWeight: 700, color: "#dc2626", marginTop: 2 }}>₹{pending}</span>
                                    </div>
                                    {totalPaid > 0 && (
                                        <button
                                            onClick={this.downloadAllPayments}
                                            title="Download all payment history"
                                            style={{ marginLeft: "auto", alignSelf: "center", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 700, color: "#1d4ed8", cursor: "pointer", display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}
                                        >
                                            <i className="fas fa-download" /> Download All History
                                        </button>
                                    )}
                                </div>
                            );
                        })()}
                    </div>

                    {/* ── Full Payment ── */}
                    {this.state.paymentArray.some(t => t.is_paid != 1) && this.state.termsValue && (
                        (() => {
                            const totalPending = this.state.paymentArray.reduce((s, t) => {
                                if (t.is_paid == 1) return s;
                                if (!t.isDueEntry) {
                                    const hasDue = this.state.paymentArray.some(d => d.isDueEntry && d.dueFromTermNo == t.term_no && d.is_paid != 1);
                                    if (hasDue) return s;
                                }
                                const net = (t.term_amount || 0) - (t.term_discount || 0);
                                return s + Math.max(0, net - (t.paid_amount || 0));
                            }, 0);
                            return (
                                <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #bbf7d0", padding: "14px 20px", marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", boxShadow: "0 1px 4px rgba(16,185,129,0.07)" }}>
                                    <div style={{ flex: 1, minWidth: 120 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: "#059669", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Full Payment</div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>Total Due: <strong style={{ color: "#dc2626" }}>₹{totalPending.toLocaleString('en-IN')}</strong></div>
                                    </div>
                                    <select
                                        value={this.state.fullPaymentMode}
                                        onChange={e => this.setState({ fullPaymentMode: e.target.value })}
                                        style={{ ...S.select, width: "auto", minWidth: 180 }}
                                    >
                                        <option value="">Select Payment Mode</option>
                                        {(this.props.getPaymentModes || []).map(p => (
                                            <option key={p.id} value={p.id}>{p.payment_mode}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={this.handleFullPayment}
                                        style={{ background: "linear-gradient(135deg, #10b981, #059669)", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}
                                    >
                                        Pay All (₹{totalPending.toLocaleString('en-IN')})
                                    </button>
                                </div>
                            );
                        })()
                    )}

                    {/* ── Term Cards ── */}
                    {this.state.paymentArray.length > 0 && this.state.termsValue && (
                        <div>
                            {/* <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
                                Payment Schedule &nbsp;
                                <span style={{ background: "#ede9fe", color: "#6d28d9", borderRadius: 20, padding: "1px 10px", fontSize: 11 }}>
                                    {paidCount}/{totalRegular} paid
                                </span>
                                {partialCount > 0 && (
                                    <span style={{ background: "#fef3c7", color: "#b45309", borderRadius: 20, padding: "1px 10px", fontSize: 11, marginLeft: 4 }}>
                                        {partialCount} partial
                                    </span>
                                )}
                                {dueCount > 0 && (
                                    <span style={{ background: "#ffedd5", color: "#c2410c", borderRadius: 20, padding: "1px 10px", fontSize: 11, marginLeft: 4 }}>
                                        {dueCount} due
                                    </span>
                                )}
                            </div> */}

                            {this.state.paymentArray.map((item, i) => {
                                const isDueEntry = !!item.isDueEntry;
                                const isPaid = item.is_paid == 1;
                                // Both regular terms and due entries can carry a discount.
                                const termDiscount = parseInt(item.term_discount) || 0;
                                // Net payable is what actually has to be collected for the row after the discount.
                                const netPayable = item.term_amount - termDiscount;
                                const paidAmountForTerm = item.paid_amount || 0;
                                const isPartiallyPaid = !isDueEntry && !isPaid && paidAmountForTerm > 0 && paidAmountForTerm < netPayable;
                                const remainingAmount = netPayable - paidAmountForTerm;

                                // Chain helpers for due entries (order by array position, not term_no)
                                const chainDues = isDueEntry
                                    ? this.state.paymentArray
                                        .filter(d => d.isDueEntry && d.dueFromTermNo == item.dueFromTermNo)
                                    : [];
                                const dueSeqNo = isDueEntry ? chainDues.findIndex(d => d === item) + 1 : 0;
                                // Only the last unpaid due in the chain (by array position) is active for payment
                                const isLastDueInChain = !isDueEntry || !this.state.paymentArray.some(
                                    (d, di) => d.isDueEntry && d.dueFromTermNo == item.dueFromTermNo && di > i && d.is_paid != 1
                                );

                                // A term is "settled" when fully paid OR partially paid with a due entry tracking the balance
                                const isSettled = (p) => {
                                    if (p.isDueEntry) return p.is_paid == 1;
                                    const hasDue = this.state.paymentArray.some(d => d.isDueEntry && d.dueFromTermNo == p.term_no);
                                    return p.is_paid == 1 || (hasDue && (p.paid_amount || 0) > 0);
                                };
                                const allPrevSettled = this.state.paymentArray.slice(0, i).every(p => isSettled(p));
                                const isNextToPay = !isDueEntry && allPrevSettled && !isPaid && !isPartiallyPaid;
                                const canPay = !isDueEntry && !isPaid && !isPartiallyPaid;
                                const isPayEnabled = canPay;
                                const showPayButton = canPay || (isDueEntry && !isPaid && isLastDueInChain);

                                // The final unpaid regular term is the balancer: its amount is whatever
                                // remains of the committed amount after every other term. Lock the Amount
                                // input only once ALL terms above it are settled — at that point there's no
                                // later editable term to absorb a redistribution, so changing it would only
                                // break the total. (e.g. 4 terms, 1-3 paid → term 4 locked.) While terms
                                // above are still unpaid, the amount stays editable so splits can be adjusted.
                                const isLockedForRedistrib = (t) => t.is_paid == 1 || (t.paid_amount || 0) > 0 || this.state.paymentArray.some(d => d.isDueEntry && d.dueFromTermNo == t.term_no);
                                const hasLaterEditableTerm = this.state.paymentArray.some((t, idx) => idx > i && !t.isDueEntry && !isLockedForRedistrib(t));
                                const isLastEditableTerm = !isDueEntry && !isPaid && !isPartiallyPaid && !hasLaterEditableTerm && allPrevSettled;
                                const isAmountLocked = isPaid || isDueEntry || isLastEditableTerm;

                                const termTitle = isDueEntry
                                    ? (chainDues.length > 1 ? `Due ${dueSeqNo} (Term ${item.dueFromTermNo})` : `Due Amount (Term ${item.dueFromTermNo})`)
                                    : `Term ${item.term_no}`;

                                const cardStyle = isPaid ? S.termCardPaid
                                    : isDueEntry ? S.termCardDue
                                        : isPartiallyPaid ? S.termCardPartial
                                            : isNextToPay ? S.termCardNext
                                                : S.termCardPending;

                                let dateString = '';
                                if (item.term_date && item.term_date !== "Invalid date" && item.term_date !== "1970-01-01") {
                                    dateString = moment.unix(item.term_date).format('YYYY-MM-DD');
                                }

                                return (
                                    <div key={`${item.term_no}-${i}`} style={cardStyle}>
                                        {/* Card header */}
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                            <span style={S.termLabel}>{termTitle}</span>
                                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                {isPaid && <span style={S.paidBadge}>Paid</span>}
                                                {isPartiallyPaid && <span style={S.partialBadge}>Partially Paid</span>}
                                                {isDueEntry && !isPaid && <span style={S.dueBadge}>Due Amount</span>}
                                                {!isPaid && !isPartiallyPaid && !isDueEntry && isNextToPay && <span style={S.nextBadge}>Next Due</span>}
                                                {!isPaid && !isPartiallyPaid && !isDueEntry && !isNextToPay && (
                                                    <span style={{ background: "#f3f4f6", color: "#9ca3af", borderRadius: 20, padding: "2px 12px", fontSize: 11, fontWeight: 600 }}>Pending</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Paid / Due info bar — shown on every row that has any payment */}
                                        {(isPaid || isPartiallyPaid || paidAmountForTerm > 0) && (
                                            <div style={{
                                                display: "flex", gap: 16, marginBottom: 10,
                                                background: isPaid ? "#dcfce7" : isDueEntry ? "#ffedd5" : "#fef3c7",
                                                borderRadius: 8, padding: "6px 12px", fontSize: 12, flexWrap: "wrap",
                                            }}>
                                                {isPaid && (
                                                    <span style={{ color: "#15803d" }}>
                                                        Term Amount: <strong>₹{item.term_amount}</strong>
                                                    </span>
                                                )}
                                                <span style={{ color: isPaid ? "#15803d" : isDueEntry ? "#c2410c" : "#92400e" }}>
                                                    Paid Amount: <strong>₹{paidAmountForTerm}</strong>
                                                </span>
                                                {!isPaid && (
                                                    <span style={{ color: isDueEntry ? "#c2410c" : "#b45309" }}>
                                                        Remaining: <strong>₹{remainingAmount}</strong>
                                                    </span>
                                                )}

                                            </div>
                                        )}

                                        <Row>
                                            {/* 1. Amount */}
                                            {(() => {
                                                const termKey = isDueEntry ? `${item.term_no}_due` : `${item.term_no}`;
                                                return (
                                                    <Col lg={2} md={3} sm={6} className="mb-2">
                                                        <label style={S.label}>Amount</label>
                                                        <input
                                                            type="number"
                                                            autoComplete="off"
                                                            value={this.state.termInputValues[termKey] !== undefined ? this.state.termInputValues[termKey] : (item.term_amount || "")}
                                                            disabled={isAmountLocked}
                                                            onChange={e => {
                                                                const v = e.target.value;
                                                                this.setState(prev => ({ termInputValues: { ...prev.termInputValues, [termKey]: v } }));
                                                                const parsed = parseInt(v);
                                                                if (!isNaN(parsed) && parsed > 0) {
                                                                    this.updateTermAmount(v, item.term_no, isDueEntry);
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                const v = this.state.termInputValues[termKey];
                                                                if (v !== undefined) {
                                                                    this.updateTermAmount(v, item.term_no, isDueEntry);
                                                                    this.setState(prev => {
                                                                        const t = { ...prev.termInputValues };
                                                                        delete t[termKey];
                                                                        return { termInputValues: t };
                                                                    });
                                                                }
                                                            }}
                                                            style={isAmountLocked ? S.inputDisabled : S.input}
                                                        />
                                                        {item.termAmountError && <div style={S.errorText}>{item.termAmountError}</div>}
                                                    </Col>
                                                );
                                            })()}

                                            {/* 1b. Discount — applies to regular terms and due entries alike */}
                                            {(() => {
                                                const isDiscountLocked = isPaid || isPartiallyPaid || paidAmountForTerm > 0;
                                                return (
                                                    <Col lg={2} md={3} sm={6} className="mb-2">
                                                        <label style={S.label}>{isDueEntry ? "Due Discount" : "Term Discount"}</label>
                                                        <input
                                                            type="number"
                                                            autoComplete="off"
                                                            min={0}
                                                            max={item.term_amount}
                                                            value={termDiscount || ""}
                                                            disabled={isDiscountLocked}
                                                            onChange={e => this.updateTermDiscount(e.target.value, i)}
                                                            style={isDiscountLocked ? S.inputDisabled : S.input}
                                                        />
                                                        {termDiscount > 0 && (
                                                            <div style={{ fontSize: 10, color: "#059669", marginTop: 2 }}>Payable: ₹{netPayable}</div>
                                                        )}
                                                    </Col>
                                                );
                                            })()}

                                            {/* 2. Paid Amount (read-only, shown when any amount has been paid) */}
                                            {paidAmountForTerm > 0 && (
                                                <Col lg={2} md={3} sm={6} className="mb-2">
                                                    <label style={S.label}>Paid Amount</label>
                                                    <div style={{ ...S.inputDisabled, display: "flex", alignItems: "center", fontWeight: 700, color: isPaid ? "#15803d" : isDueEntry ? "#c2410c" : "#b45309" }}>
                                                        ₹{paidAmountForTerm}
                                                    </div>
                                                </Col>
                                            )}

                                            {/* 3. Due Date */}
                                            {!isDueEntry && (
                                                <Col lg={2} md={3} sm={6} className="mb-2">
                                                    <label style={S.label}>Due Date</label>
                                                    {(isPaid || isPartiallyPaid) ? (
                                                        <div style={{ ...S.inputDisabled, display: "flex", alignItems: "center" }}>
                                                            {item.term_date ? fmtDate(item.term_date) : '—'}
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="date"
                                                            name="term_date"
                                                            autoComplete="off"
                                                            value={dateString}
                                                            min={currentDate}
                                                            onChange={e => this.handleDateChange(e, i, e.target.value)}
                                                            style={S.input}
                                                        />
                                                    )}
                                                    {item.dateError && <div style={S.errorText}>{item.dateError}</div>}
                                                </Col>
                                            )}

                                            {/* 3. Payment Mode */}
                                            {(isPaid || !isDueEntry || (isDueEntry && !isPaid)) && (
                                                <Col lg={2} md={3} sm={6} className="mb-2">
                                                    <label style={S.label}>Payment Mode</label>
                                                    <select name="paymentMode" disabled={isPaid || isPartiallyPaid} onChange={e => this.paymentModeAndComments(e, item.term_no, isDueEntry)} style={(isPaid || isPartiallyPaid) ? S.inputDisabled : S.select}>
                                                        <option value="0">Select</option>
                                                        {(this.props.getPaymentModes || []).map(payment => (
                                                            <option key={payment.id} value={payment.id} selected={item.payment_mode_id == payment.id}>{payment.payment_mode}</option>
                                                        ))}
                                                    </select>
                                                </Col>
                                            )}

                                            {/* 4. Pay Amount — hidden when the discount fully waives the row (nothing to pay) */}
                                            {!isPaid && showPayButton && remainingAmount > 0 && (
                                                <Col lg={2} md={3} sm={6} className="mb-2">
                                                    <label style={S.label}>Pay Amount</label>
                                                    <input
                                                        type="number"
                                                        autoComplete="off"
                                                        min={1}
                                                        max={remainingAmount}
                                                        value={(() => { const k = isDueEntry ? `${item.term_no}_due` : item.term_no; return this.state.payInputAmounts[k] !== undefined ? this.state.payInputAmounts[k] || "" : remainingAmount || ""; })()}
                                                        onChange={e => {
                                                            const k = isDueEntry ? `${item.term_no}_due` : item.term_no;
                                                            const raw = e.target.value;
                                                            const parsed = parseInt(raw) || 0;
                                                            if (raw === "" || parsed <= 0) {
                                                                this.setState(prev => ({
                                                                    payInputAmounts: { ...prev.payInputAmounts, [k]: "" }
                                                                }));
                                                            } else {
                                                                const val = Math.min(parsed, remainingAmount);
                                                                this.setState(prev => ({
                                                                    payInputAmounts: { ...prev.payInputAmounts, [k]: val }
                                                                }));
                                                            }
                                                        }}
                                                        style={S.input}
                                                    />
                                                    <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>Max: ₹{remainingAmount}</div>
                                                </Col>
                                            )}

                                            {/* 5. Paid Date  6. Comments */}
                                            {(isPaid || !isDueEntry || (isDueEntry && !isPaid)) && (<>
                                                <Col lg={2} md={3} sm={6} className="mb-2">
                                                    <label style={S.label}>Paid Date</label>
                                                    {(isPaid || isPartiallyPaid) ? (
                                                        <div style={{ ...S.inputDisabled, display: "flex", alignItems: "center" }}>
                                                            {item.paid_date ? fmtDate(item.paid_date) : moment().format('DD/MM/YYYY')}
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="date"
                                                            name="paidDate"
                                                            autoComplete="off"
                                                            value={item.paid_date ? moment.unix(item.paid_date).format('YYYY-MM-DD') : currentDate}
                                                            onChange={e => this.handleDateChange(e, i, e.target.value)}
                                                            style={S.input}
                                                        />
                                                    )}
                                                </Col>
                                                {/* Others (internal amount) — regular terms only; must be less than the pay amount */}
                                                {!isDueEntry && (() => {
                                                    const payAmt = this.payAmountForRow(item);
                                                    return (
                                                        <Col lg={2} md={3} sm={6} className="mb-2">
                                                            <label style={S.label}>Term Fee</label>
                                                            <input
                                                                type="number"
                                                                autoComplete="off"
                                                                min={0}
                                                                max={Math.max(0, payAmt - 1)}
                                                                value={item.internal_amount || ""}
                                                                disabled={isPaid}
                                                                onChange={e => this.updateInternalAmount(e.target.value, i)}
                                                                style={isPaid ? S.inputDisabled : S.input}
                                                            />
                                                            {!isPaid && <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>Less than ₹{payAmt}</div>}
                                                        </Col>
                                                    );
                                                })()}
                                                <Col lg={2} md={3} sm={6} className="mb-2">
                                                    <label style={S.label}>Reference Ids</label>
                                                    <textarea style={{ ...S.input, resize: "none" }} value={item.comments} rows="1" name="comments" disabled={isPaid || isPartiallyPaid} onChange={e => this.paymentModeAndComments(e, item.term_no, isDueEntry)} />
                                                </Col>
                                            </>)}

                                            {/* Action button */}
                                            <Col lg={2} md={3} sm={6} className="mb-2 d-flex flex-column justify-content-end">
                                                {isPaid ? (
                                                    <span style={{ fontSize: 11, color: "#15803d", fontWeight: 600 }}>Payment Successful</span>
                                                ) : isDueEntry ? (() => {
                                                    if (!isLastDueInChain) {
                                                        return <span style={{ fontSize: 11, color: "#c2410c", fontWeight: 600 }}>Due record added ↓</span>;
                                                    }
                                                    // New due entries (null ID) created in this session are always enabled.
                                                    // Saved entries (have ID) must exist in defaultArray to be enabled.
                                                    const isDueEnabled = !item.student_payment_log_id
                                                        || this.state.defaultArray.some(d => d.isDueEntry && d.student_payment_log_id == item.student_payment_log_id);
                                                    return isDueEnabled ? (
                                                        <button style={S.makePayBtn} onClick={e => this.paymentCheckBox(e, item.term_no, true)}>{remainingAmount > 0 ? "Pay Due" : "Settle (Waived)"}</button>
                                                    ) : (
                                                        <button disabled style={{ ...S.makePayBtn, opacity: 0.45, cursor: "not-allowed" }}>Pay Due</button>
                                                    );
                                                })() : isPartiallyPaid ? (
                                                    <span style={{ fontSize: 11, color: "#b45309", fontWeight: 600 }}>Due record added ↓</span>
                                                ) : canPay ? (
                                                    <button
                                                        disabled={!isPayEnabled}
                                                        style={isPayEnabled ? S.makePayBtn : { ...S.makePayBtn, opacity: 0.45, cursor: "not-allowed" }}
                                                        onClick={e => this.paymentCheckBox(e, item.term_no, false)}
                                                    >{remainingAmount > 0 ? "Make Payment" : "Settle (Waived)"}</button>
                                                ) : null}
                                                {isPaid && item.student_payment_log_id && this.state.defaultArray.some(d => d.student_payment_log_id == item.student_payment_log_id && d.is_paid == 1) && (<>
                                                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151", cursor: "pointer", marginBottom: 4 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!this.state.showFeeTypeInPdf[item.term_no]}
                                                            onChange={e => {
                                                                const checked = e.target.checked;
                                                                this.setState(prev => ({
                                                                    showFeeTypeInPdf: { ...prev.showFeeTypeInPdf, [item.term_no]: checked },
                                                                }));
                                                            }}
                                                            style={{ accentColor: "#6366f1", width: 14, height: 14 }}
                                                        />
                                                        Hide Fee Type
                                                    </label>
                                                    <button style={{ ...S.downloadBtn, background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8" }} onClick={() => {
                                                        const itemDay = item.paid_date ? moment.unix(parseInt(item.paid_date)).format('YYYY-MM-DD') : null;
                                                        const sameBatch = itemDay ? this.state.defaultArray.filter(d =>
                                                            d.is_paid == 1 &&
                                                            d.paid_date &&
                                                            moment.unix(parseInt(d.paid_date)).format('YYYY-MM-DD') === itemDay
                                                        ) : [item];
                                                        this.viewPDF(sameBatch.length > 1 ? sameBatch : item);
                                                    }}>
                                                        <i className="fas fa-eye" style={{ marginRight: 5, fontSize: 11 }} />
                                                        Receipt
                                                    </button>
                                                </>)}
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>

                {/* ── Footer ── */}
                <Modal.Footer style={{ background: "#fff", borderTop: "1px solid #f3f4f6", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, position: "sticky", bottom: 0, zIndex: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {this.state.currentStep === 5 ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 14px", fontSize: 13 }}>
                                <i className="fas fa-check-circle" /> Payment data saved successfully
                            </div>
                        ) : this.state.submitError ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 14px", fontSize: 13 }}>
                                <i className="fas fa-exclamation-circle" /> {this.state.submitError}
                            </div>
                        ) : null}
                    </div>
                    {this.state.currentStep === 5 ? (
                        <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                            {!allPaid && (
                                <button
                                    onClick={this.handleMakeAnotherPayment}
                                    style={{ ...S.submitBtn, background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)", boxShadow: "0 4px 12px rgba(99,102,241,0.3)" }}
                                >
                                    Make Another Payment
                                </button>
                            )}
                            <button onClick={this.SetpageLoad} style={{ ...S.submitBtn, background: "#f3f4f6", color: "#374151", boxShadow: "none" }}>Close</button>
                        </div>
                    ) : this.state.termsValue && (!allPaid || this.state.pendingChanges) ? (
                        <button
                            onClick={() => this.handleFormSubmit()}
                            disabled={this.state.isSubmitting}
                            style={{ ...(this.state.isSubmitting ? S.submitBtnDisabled : S.submitBtn), flexShrink: 0 }}
                        >
                            {this.state.loadingSpinner === 1 && (
                                <span className="spinner-border spinner-border-sm mr-2" role="status" />
                            )}
                            Save & Submit
                        </button>
                    ) : null}
                </Modal.Footer>

                <FeeReceiptModal
                    show={this.state.receiptModal}
                    onHide={() => this.setState({ receiptModal: false, receiptItem: null })}
                    item={this.state.receiptItem}
                    studentName={this.props.studentName}
                    parentName={this.props.stateData?.parent_name}
                    organisationProfile={this.state.orgProfile}
                    getPaymentModes={this.props.getPaymentModes}
                    stateData={this.props.stateData}
                />
            </Modal>
        );
    }
}

export default withRouter(withApollo(compose(
    graphql(ADD_TERMS, { name: "addTerms" }),
    graphql(UPDATE_TERMS, { name: "updateTerms" }),
    graphql(GET_FEE_TYPES, {
        name: "getFeeTypes",
        options: () => ({ variables: { institute_id: parseInt(Cookies.get("institutionid")) } }),
    })
)(FeeStatusModal)));
